import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { mockGames } from '../mock/mockData';
import { ArrowLeft, ChevronLeft, ChevronRight, X } from 'lucide-react';

const GameDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [lightboxIndex, setLightboxIndex] = useState(null);

  // Scroll to top when component mounts
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const game = mockGames.find(g => g.gamePath === (id));
  if (!game) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl text-white mb-4">{t('games.gameNotFound')}</h2>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            {t('games.backToGames')}
          </button>
        </div>
      </div>
    );
  }

  const title = language === 'cz' && game.titleCz != null ? game.titleCz : game.title;
  const description = language === 'cz' ? game.descriptionCz : game.description;
  const genre = language === 'cz' ? game.genreCz : game.genre;
  const status = language === 'cz' ? game.statusCz : game.status;

  const openLightbox = (index) => {
    setLightboxIndex(index);
  };

  const closeLightbox = () => {
    setLightboxIndex(null);
  };

  const goToPrevImage = () => {
    setLightboxIndex((prev) => (prev - 1 + game.gallery.length) % game.gallery.length);
  };

  const goToNextImage = () => {
    setLightboxIndex((prev) => (prev + 1) % game.gallery.length);
  };

  return (
    <div className="min-h-screen bg-black pt-20">
      {/* Hero Section */}
      <div className="relative h-[60vh] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${game.thumbnail})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/70 to-black"></div>
        </div>

        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-12">
          <button
            onClick={() => {
              navigate('/');
              setTimeout(() => {
                const gamesSection = document.getElementById('games');
                if (gamesSection) {
                  gamesSection.scrollIntoView({ behavior: 'smooth' });
                }
              }, 100);
            }}
            className="inline-flex items-center space-x-2 text-gray-300 hover:text-red-500 transition-colors mb-8 w-fit"
          >
            <ArrowLeft size={20} />
            <span>{t('games.backToGames')}</span>
          </button>

          <div className="inline-block px-4 py-1 bg-red-600/90 backdrop-blur-sm rounded-full text-white text-sm font-semibold mb-4 w-fit">
            {status}
          </div>

          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
            {title}
          </h1>

          <p className="text-red-400 text-lg uppercase tracking-wider font-semibold">
            {genre} • {game.platform}
          </p>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ">
        {/* Details Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mb-16">
          {/* Description */}
          <div className="lg:col-span-2">
            <h2 className="text-2xl font-bold text-white mb-4">{t('games.details')}</h2>
            <p className="text-gray-400 text-lg leading-relaxed mb-8">
              {description}
            </p>

            <div className="grid grid-cols-2 gap-2 md:gap-4 lg:gap-6">
              <div className="bg-gray-900/50 border border-red-900/20 rounded-lg p-6">
                <p className="text-gray-500 text-sm mb-2">{t('games.genre')}</p>
                <p className="text-white font-semibold">{genre}</p>
              </div>
              <div className="bg-gray-900/50 border border-red-900/20 rounded-lg p-6">
                <p className="text-gray-500 text-sm mb-2">{t('games.platform')}</p>
                <p className="text-white font-semibold">{game.platform}</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 md:gap-4 lg:gap-6 md:py-4 lg:py-6 py-2">
              <div className="bg-gray-900/50 border border-red-900/20 rounded-lg p-6">
                <p className="text-gray-500 text-sm mb-2">{t('games.releaseDate')}</p>
                <p className="text-white font-semibold">{game.releaseDate}</p>
              </div>
              <div className="bg-gray-900/50 border border-red-900/20 rounded-lg p-6">
                <p className="text-gray-500 text-sm mb-2">{t('games.locale')}</p>
                <p className="text-white font-semibold">{game.locale}</p>
              </div>
              <div className="bg-gray-900/50 border border-red-900/20 rounded-lg p-6">
                <p className="text-gray-500 text-sm mb-2">{t('games.status')}</p>
                <p className="text-white font-semibold">{status}</p>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div>
            <div className="bg-gray-900/50 border border-red-900/20 rounded-lg p-6 sticky top-24">
              <h3 className="text-xl font-bold text-white mb-4">{t('games.quickInfo')}</h3>
              <div className="space-y-4">
                {game.liveDemo && (
                  <a
                    href={game.liveDemo}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-center w-full px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors"
                  >
                    {t('games.viewGame')}
                  </a>
                )}

                {game.wishlist && (
                  <a
                    href={game.wishlist}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-center w-full px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors"
                  >
                    {t('games.wishlist')}
                  </a>
                )}
                {game.award && (<div className="w-full px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white font-semibold rounded-lg transition-colors">
                  Award:
                  <img src={game.award} alt="award" className="mt-2 w-full rounded-md" />
                </div>)}
              </div>
            </div>
          </div>
        </div>

        {/* Gallery */}
        <div>
          <h2 className="text-2xl font-bold text-white mb-6">{t('games.gallery')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {game.gallery.map((image, index) => (
              <div
                key={index}
                onClick={() => openLightbox(index)}
                className="relative h-64 rounded-lg overflow-hidden cursor-pointer group"
              >
                <img
                  src={image}
                  alt={`${title} screenshot ${index + 1}`}
                  className="w-full h-full object-cover transition-transform group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors"></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center">
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
          >
            <X size={24} />
          </button>

          <button
            onClick={goToPrevImage}
            className="absolute left-4 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
          >
            <ChevronLeft size={24} />
          </button>

          <button
            onClick={goToNextImage}
            className="absolute right-4 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
          >
            <ChevronRight size={24} />
          </button>

          <img
            src={game.gallery[lightboxIndex]}
            alt={`${title} screenshot ${lightboxIndex + 1}`}
            className="max-w-[90vw] max-h-[90vh] object-contain"
          />

          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm">
            {lightboxIndex + 1} / {game.gallery.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default GameDetail;